import React from 'react';
import { FONT_SIZE, FONT_WEIGHT, RADIUS, SPACING, type TagTheme, type ThemeTokens } from '../../constants/constants';
import { useThemeTokens } from '../../hooks/UseThemeTokens';

// ─── 상태 색 매핑(한 곳) ──────────────────────────────────────────────────────

/** 의미 4종 + 중립 — 배지 · 점 · 윤곽선이 모두 이 다섯 가지 색만 쓴다(디자인 시스템 §3-1). */
export type StatusTone = 'info' | 'success' | 'warning' | 'danger' | 'neutral';

/**
 * 톤 → 배경 · 글자 · 테두리. 기존 tint* · line* 짝을 그대로 쓴다(새 색 값 없음).
 * 다섯 쌍 모두 글자 / 배경 4.5:1 이상(라이트 · 다크, `StatusBadge.test.tsx`).
 */
export function toneTheme(t: ThemeTokens, tone: StatusTone): TagTheme {
  switch (tone) {
    case 'info':    return { bg: t.tintInfo,    text: t.accent,        border: t.lineAccent };
    case 'success': return { bg: t.tintSuccess, text: t.success,       border: t.lineSuccess };
    case 'warning': return { bg: t.tintWarning, text: t.warning,       border: t.lineWarning };
    case 'danger':  return { bg: t.tintDanger,  text: t.danger,        border: t.lineDanger };
    case 'neutral': return { bg: t.bgRaised,    text: t.textSecondary, border: t.border };
  }
}

/** 업무 상태 — 이 줄을 두고 사람이 무엇을 해야 하나(알약). */
export type WorkStatus =
  | 'available' | 'pending' | 'queued' | 'hold' | 'excluded' | 'review' | 'scheduled' | 'applied' | 'kept';
/** 동기화 상태 — 네이버 · PriceHub 와 맞췄나(점 + 글자). */
export type SyncStatus = 'synced' | 'failed' | 'skipped' | 'uncollected';
export type BadgeStatus = WorkStatus | SyncStatus;

/** 상태 → 톤 · 기본 글자. 상태 색은 여기서만 정한다(같은 상태가 화면마다 다른 색이 되지 않게). */
export const STATUS_META: Record<BadgeStatus, { tone: StatusTone; label: string }> = {
  available:   { tone: 'info',    label: '요청 가능' },
  pending:     { tone: 'warning', label: '확정 대기' },
  queued:      { tone: 'neutral', label: '대기' },
  hold:        { tone: 'neutral', label: '보류' },
  excluded:    { tone: 'neutral', label: '대상 아님' },
  review:      { tone: 'warning', label: '확인 필요' },
  scheduled:   { tone: 'info',    label: '반영 예정' },
  applied:     { tone: 'success', label: '적용됨' },
  kept:        { tone: 'neutral', label: '유지' },
  synced:      { tone: 'success', label: '반영됨' },
  failed:      { tone: 'danger',  label: '실패' },
  skipped:     { tone: 'warning', label: '건너뜀' },
  uncollected: { tone: 'neutral', label: '미수집' },
};

// ─── StatusBadge ──────────────────────────────────────────────────────────────

export interface StatusBadgeProps {
  /**
   * work — 업무 상태(알약: 연한 채움 + 글자), sync — 동기화 상태(점 + 글자, 채움 없음),
   * tag — 분류 태그(네모 윤곽선). 축이 다르면 모양이 달라 한 칸에 섞여도 구분된다(개편 §2-4).
   */
  axis: 'work' | 'sync' | 'tag';
  status: BadgeStatus;
  /** 기본 글자 대신(예: '✓ 적용됨' · '③ 건너뜀 — 검토 필요'). 색은 status 를 따른다. */
  label?: React.ReactNode;
  /** 자세한 사유 — 툴팁(title)과 읽어 주는 이름에. */
  detail?: string;
  testId?: string;
  /** 키보드로 툴팁을 열 수 있게(감싸는 Tooltip 이 있을 때). */
  focusable?: boolean;
}

/**
 * 상태 배지(디자인 시스템 DS-1). **색만으로 구분하지 않는다** — 글자가 반드시 들어간다.
 * AppTag 는 그대로 쓰되(호환 유지) 같은 톤 표(`toneTheme`)를 써서 색이 어긋나지 않게 한다(결정 D9).
 */
export const StatusBadge = React.forwardRef<HTMLSpanElement, StatusBadgeProps & React.HTMLAttributes<HTMLSpanElement>>(
  ({ axis, status, label, detail, testId, focusable, style, ...rest }, ref) => {
    const t = useThemeTokens();
    const meta = STATUS_META[status];
    const c = toneTheme(t, meta.tone);
    const base: React.CSSProperties = {
      display: 'inline-flex', alignItems: 'center', gap: SPACING.xxs, maxWidth: '100%',
      fontSize: FONT_SIZE.xs, fontWeight: FONT_WEIGHT.semibold, lineHeight: 1.5,
      whiteSpace: 'normal', wordBreak: 'keep-all', color: c.text,
    };
    const shape: React.CSSProperties = axis === 'work'
      ? { background: c.bg, border: `1px solid ${c.border}`, borderRadius: 999, padding: `0 ${SPACING.xs}px` }
      : axis === 'tag'
        ? { background: 'transparent', border: `1px solid ${c.border}`, borderRadius: RADIUS.sm, padding: `0 ${SPACING.xxs}px` }
        : { background: 'transparent', padding: 0 };
    return (
      <span
        ref={ref}
        data-testid={testId}
        data-status={status}
        data-axis={axis}
        title={detail}
        tabIndex={focusable ? 0 : undefined}
        {...rest}
        style={{ ...base, ...shape, ...style }}
      >
        {axis === 'sync' && (
          <span aria-hidden style={{ width: 7, height: 7, borderRadius: '50%', background: c.text, flex: 'none' }} />
        )}
        {label ?? meta.label}
      </span>
    );
  },
);
StatusBadge.displayName = 'StatusBadge';
